import React, { useContext, useEffect, useMemo } from 'react';

import useSWR from 'swr';

import { doesArrayHaveNullValues } from '../../../utils/product_utils';

import { preview_result_from_v1 } from '../V2Assays/conversions';
import { swrAssayBaselineSummarizer } from '../../../utils/api_functions/assaySummarize';
import { swrPreviewBaselineBinning } from '../../../utils/api_functions/assayPreviewBaselineBinning';
import { swrAssayRunRequest } from '../../../utils/api_functions/assayRunRequest';
import useClientContext from '../../contexts/ClientData/ClientData';

const AssayRunContext = React.createContext({
  assayRunData: {},
  baselineSummary: {},
  assayRunError: null,
  summaryError: null,
  summaryLoading: false,
  assayRunLoading: false,
});

/**
 * @deprecated Assays V1 Logic
 */
const useAssayV1Summarizer = (assayConfig, isAssaysV1Enabled) => {
  // Prevent errors from incomplete edges and bin weight arrays
  const badEdges = assayConfig?.summarizer?.provided_edges?.length && doesArrayHaveNullValues(assayConfig?.summarizer?.provided_edges);
  const badWeights = assayConfig?.summarizer?.bin_weights?.length && doesArrayHaveNullValues(assayConfig?.summarizer?.bin_weights)

  const summarizer = assayConfig?.summarizer;
  const baseline = assayConfig?.baseline;
  const iopath = assayConfig.getIOPath();

  const swrValidator = () => (isAssaysV1Enabled && summarizer && baseline && iopath && !badEdges && !badWeights) ? 'assay-baseline-summary-v1' : null;

  const { data, error, isLoading: loading } = useSWR(swrValidator, () => swrAssayBaselineSummarizer([summarizer, baseline, iopath]), {
    refreshInterval: 10000,
    loadingTimeout: 5000,
  });

  return {
    baselineSummary: data,
    error,
    loading,
  }
}

const useAssayV2Summarizer = (assayConfig, isAssaysV2Enabled) => {
  // Prevent errors from incomplete edges and bin weight arrays
  const badEdges = assayConfig?.summarizer?.provided_edges?.length && doesArrayHaveNullValues(assayConfig?.summarizer?.provided_edges);
  const badWeights = assayConfig?.summarizer?.bin_weights?.length && doesArrayHaveNullValues(assayConfig?.summarizer?.bin_weights)

  const summarizer = assayConfig?.summarizer;
  const baseline = assayConfig?.baseline;
  const iopath = assayConfig.getIOPath();

  const swrValidator = () => (isAssaysV2Enabled && summarizer && baseline && iopath && !badEdges && !badWeights) ? 'assay-baseline-summary-v2' : null;

  const { data, error, isLoading: loading } = useSWR(swrValidator, () => swrPreviewBaselineBinning([assayConfig]), {
    refreshInterval: 10000,
    loadingTimeout: 5000,
  });

  return {
    baselineSummary: data,
    error,
    loading,
  }
}

const useAssayBaselineSummary = (assayConfig, isAssaysV2Enabled = false) => {
  const {
    baselineSummary: v1Summary,
    error: v1Error,
    loading: v1Loading,
  } = useAssayV1Summarizer(assayConfig, !isAssaysV2Enabled);
  const {
    baselineSummary: v2Summary,
    error: v2Error,
    loading: v2Loading,
  } = useAssayV2Summarizer(assayConfig, isAssaysV2Enabled)
  if (isAssaysV2Enabled) {
    return {
      baselineSummary: v2Summary,
      error: v2Error,
      loading: v2Loading,
    };
  } else {
    /**
     * @deprecated Assays V1 Logic
     */
    return {
      baselineSummary: v1Summary,
      error: v1Error,
      loading: v1Loading,
    }
  }
};

// TODO - Should be using a separate window configuration from creation
// Used for Preview - start and width should be based on the window inputs in the modal. Interval should be the advanced settings
const useAssayRunRequest = (assayConfig, baselineSummary, startTimestamp, endTimestamp, isAssaysV2Enabled) => {
  // Prevent flickering due to all data loading if no start is specified.
  // const windowStart = assayConfig?.window?.start;
  // Prevent errors from incomplete edges and bin weight arrays
  const badEdges = assayConfig?.summarizer?.provided_edges?.length && doesArrayHaveNullValues(assayConfig?.summarizer?.provided_edges);
  const badWeights = assayConfig?.summarizer?.bin_weights?.length && doesArrayHaveNullValues(assayConfig?.summarizer?.bin_weights)

  const swrValidator = () => (assayConfig && baselineSummary && startTimestamp && endTimestamp && !badEdges && !badWeights) ? 'assay-run-request' : null

  const { data, error, isLoading: loading } = useSWR(swrValidator, () => swrAssayRunRequest([assayConfig, baselineSummary, startTimestamp, endTimestamp, isAssaysV2Enabled]), {
    refreshInterval: 10000,
    loadingTimeout: 5000,
  });

  if (data?.code === 500) {
    return {
      assayRunData: [],
      error: data,
      loading,
    }
  }

  return {
    assayRunData: data,
    error,
    loading,
  }
};

export const AssayRunProvider = ({
  assayConfig,
  setBaselineSummary,
  setNextIsDisabled,
  children,
  startTimestamp,
  endTimestamp,
}) => {
  const { isAssaysV2Enabled } = useClientContext();
  const {
    baselineSummary = null,
    error: summaryError,
    loading: summaryLoading,
  } = useAssayBaselineSummary(assayConfig, isAssaysV2Enabled);

  const {
    assayRunData,
    error: assayRunError,
    loading: assayRunLoading,
  } = useAssayRunRequest(assayConfig, baselineSummary, startTimestamp, endTimestamp, isAssaysV2Enabled);

  const contextValue = useMemo(() => {
    let run_data = assayRunData;

    /**
     * @deprecated Assays V1 Logic
     */
    if (isAssaysV2Enabled) {
      run_data = assayRunData?.map(x => preview_result_from_v1(x, assayConfig, baselineSummary));
    }

    return ({
      baselineSummary,
      assayRunData: run_data,
      assayRunError,
      summaryError,
      summaryLoading,
      assayRunLoading,
    })
  }, [assayRunData, assayRunError, assayRunLoading, baselineSummary, summaryError, summaryLoading, assayConfig, isAssaysV2Enabled])

  useEffect(() => {
    setBaselineSummary(baselineSummary);
  }, [baselineSummary, setBaselineSummary])

  useEffect(() => {
    if (setNextIsDisabled) {
      setNextIsDisabled(summaryLoading);
    }
  }, [summaryLoading, setNextIsDisabled])

  return (
    <AssayRunContext.Provider value={contextValue}>
      {children}
    </AssayRunContext.Provider>
  )
}

export const useAssayRunContext = () => useContext(AssayRunContext);

export default useAssayRunContext;
